﻿import React, { useState, useCallback, useEffect } from 'react';
import {
  Sparkles,
  ShieldCheck,
  RefreshCw,
  AlertTriangle,
  Camera,
  Layers,
  ArrowRight,
  FileImage,
  RotateCw,
  RotateCcw,
  Crop,
  Archive,
  CheckCircle2,
  Paintbrush,
  Eye,
} from 'lucide-react';
import {
  BatchPhotoItem,
  RestorationVersion,
  UpscaledAsset,
  ChatMessage,
  ImageSource,
  QuadCorners,
  PhotoMediumType,
  SubjectIdentity,
} from './types';
import { analyzePhotograph, restorePhotograph, MEDIUM_LABELS, setApiAuthToken } from './services/geminiService';
import {
  processAndNormalizeImage,
  readOriginalImage,
  rotateImageSource,
  rotateImageUrl,
  createArchivalSamplePhoto,
  imageSourceFromDataUrl,
  createWatermarkedImage,
  createAnnotatedSubjectMap,
} from './services/imageUtils';
import {
  measureImageResolution,
  estimatedPrintInchesAtDpi,
  megapixelsOf,
  type ImageResolution,
} from './services/resolution';
import { detectFacesForAnalysis, faceBoundsFromClick, type DetectedFace } from './services/faceDetection';
import { createZipBlob, dataUrlToUint8Array } from './services/zipUtils';
import { ComparisonSlider } from './components/ComparisonSlider';
import { ResolutionPanel } from './components/ResolutionPanel';
import { UpscaleDialog } from './components/UpscaleDialog';
import { AnalysisCard } from './components/AnalysisCard';
import { ChatFollowUp } from './components/ChatFollowUp';
import { BatchQueueDrawer } from './components/BatchQueueDrawer';
import { PerspectiveCropModal } from './components/PerspectiveCropModal';
import { DamageMaskModal } from './components/DamageMaskModal';
import { GooglePhotosPanel, ShareActions } from './components/Integrations';
import { GooglePhotosSaveButton } from './components/GooglePhotosSaveButton';
import { AccountMenu } from './components/AccountMenu';
import { BrandLogo } from './components/BrandLogo';
import { useAuth } from './lib/AuthContext';
import { api, formatErrorForChat, KinveraApiError } from './lib/api';
import { Link } from 'react-router-dom';

const ARCHIVAL_SAMPLES = [
  {
    id: '1940s-portrait' as const,
    title: '1940s Creased Portrait (B&W / Sepia)',
    description: 'Angled photograph on wooden table, finger border artifact, deep fold crease & flash sheen',
  },
  {
    id: '1960s-group' as const,
    title: '1960s Faded Family Color Print',
    description: 'Yellowed color print with camera tilt, edge abrasions, scratches & lighting reflection',
  },
];

export const App: React.FC = () => {
  const { token, balance, refreshBalance, user, productConfig } = useAuth();
  const watermarkExportsEnabled = productConfig?.watermarkExportsEnabled !== false;
  const upscaleEnabled = productConfig?.upscaleEnabled !== false;
  const restorationsFree =
    productConfig?.restorationsCurrentlyFree ||
    balance?.restorationsCurrentlyFree ||
    productConfig?.creditMode === 'disabled' ||
    productConfig?.creditMode === 'metered';
  const stageLabel =
    (productConfig?.stage || balance?.stage) === 'beta' ? 'Kinvera Beta' : 'Kinvera Alpha';

  // Batch Queue State
  const [queue, setQueue] = useState<BatchPhotoItem[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeVersionIndex, setActiveVersionIndex] = useState<number>(0);
  const [isProcessingBatch, setIsProcessingBatch] = useState<boolean>(false);
  const [isRotating, setIsRotating] = useState<boolean>(false);
  const [isZipping, setIsZipping] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showGooglePhotos, setShowGooglePhotos] = useState<boolean>(false);
  const [subjectPlacementMode, setSubjectPlacementMode] = useState(false);
  const [locatingPersonIndex, setLocatingPersonIndex] = useState<number | null>(null);

  // Upscale state
  const [isUpscaleOpen, setIsUpscaleOpen] = useState(false);
  const [isUpscaling, setIsUpscaling] = useState(false);
  const [upscaleError, setUpscaleError] = useState<string | null>(null);
  const [upscaleStatus, setUpscaleStatus] = useState<string | null>(null);
  const [activeUpscaleId, setActiveUpscaleId] = useState<string | null>(null);
  const [activeResolution, setActiveResolution] = useState<ImageResolution | null>(null);
  /** Intrinsic size of the selected restoration revision (source for upscale). */
  const [restorationResolution, setRestorationResolution] = useState<ImageResolution | null>(
    null
  );
  const [upscaleCapabilities, setUpscaleCapabilities] = useState({
    enabled: true,
    factors: [2, 3, 4],
    maxOutputPixels: null as number | null,
    creditCost: 1,
    creditsRequired: false,
  });

  // Modals state
  const [isCropModalOpen, setIsCropModalOpen] = useState<boolean>(false);
  const [isMaskModalOpen, setIsMaskModalOpen] = useState<boolean>(false);

  // Drag and drop states
  const [isDraggingOverDropzone, setIsDraggingOverDropzone] = useState<boolean>(false);
  const [isWindowDragging, setIsWindowDragging] = useState<boolean>(false);

  // Active Photo Item Getter
  const activeItem = queue.find((item) => item.id === activeId) || null;

  useEffect(() => {
    setApiAuthToken(token);
  }, [token]);

  useEffect(() => {
    if (!upscaleEnabled) return;
    api
      .upscaleCapabilities()
      .then((caps) =>
        setUpscaleCapabilities({
          enabled: caps.enabled !== false,
          factors: caps.factors?.length ? caps.factors : [2, 3, 4],
          maxOutputPixels:
            caps.maxOutputPixels == null ? null : Number(caps.maxOutputPixels) || null,
          creditCost: Number(caps.creditCost ?? 1),
          creditsRequired: Boolean(caps.creditsRequired),
        })
      )
      .catch(() => {
        /* keep defaults — restoration must stay usable if capabilities fail */
      });
  }, [upscaleEnabled]);

  // Measure intrinsic resolution of the displayed image (restoration or selected upscale)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!activeItem || activeItem.versions.length === 0) {
        setActiveResolution(null);
        setRestorationResolution(null);
        return;
      }
      const version =
        activeItem.versions[activeVersionIndex] ||
        activeItem.versions[activeItem.versions.length - 1];
      const upscaled =
        activeUpscaleId &&
        (activeItem.upscaledAssets || []).find((a) => a.id === activeUpscaleId);
      const displayUrl = upscaled?.imageUrl || version?.imageUrl;
      if (!displayUrl) {
        setActiveResolution(null);
        return;
      }
      try {
        const [displayRes, restoreRes] = await Promise.all([
          measureImageResolution(displayUrl),
          version?.imageUrl
            ? measureImageResolution(version.imageUrl)
            : Promise.resolve(null),
        ]);
        if (!cancelled) {
          setActiveResolution(displayRes);
          setRestorationResolution(restoreRes);
        }
      } catch {
        if (!cancelled) {
          setActiveResolution(null);
          setRestorationResolution(null);
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [activeItem, activeVersionIndex, activeUpscaleId]);

  // Clear selected upscale when switching photos/revisions
  useEffect(() => {
    setActiveUpscaleId(null);
    setUpscaleStatus(null);
    setUpscaleError(null);
  }, [activeId, activeVersionIndex]);

  // Run analysis on a newly added photo without auto-restoring
  const runForensicAnalysisOnly = async (item: BatchPhotoItem): Promise<void> => {
    setQueue((prev) =>
      prev.map((q) => (q.id === item.id ? { ...q, status: 'analyzing', errorMessage: null } : q))
    );

    try {
      let detectedFaces: DetectedFace[] = [];
      try {
        detectedFaces = await detectFacesForAnalysis(item.previewImage.dataUrl);
      } catch (detectErr) {
        console.warn('Face detection skipped:', detectErr);
        detectedFaces = [];
      }

      let subjectMap: ImageSource | null = null;
      if (detectedFaces.length > 0) {
        try {
          subjectMap = await createAnnotatedSubjectMap(item.previewImage, detectedFaces);
        } catch (mapErr) {
          console.warn('Subject map annotation skipped:', mapErr);
          subjectMap = null;
        }
      }

      const { analysis: photoAnalysis, jobId, warning } = await analyzePhotograph(
        item.previewImage,
        item.originalImage,
        {
          cacheKey: item.id,
          subjectMap,
          detectedFaces,
          previewUrl: item.previewImage.dataUrl,
        }
      );
      const visible = photoAnalysis.visiblePersonCount ?? photoAnalysis.subjects?.length ?? 0;
      const mapped = photoAnalysis.mappedFaceCount ?? 0;
      const coverageNote =
        visible > 0
          ? mapped < visible
            ? ` ${visible} person(s) identified; ${mapped} automatically located — locate any remaining people on the photo if needed.`
            : ` ${visible} person(s) identified and located.`
          : '';
      const agentText = warning
        ? `Analysis complete with a warning: ${warning} Source: ${
            photoAnalysis.sourceTypeLabel || photoAnalysis.sourceType || 'unknown'
          }. Medium: "${
            photoAnalysis.photoTypeLabel || photoAnalysis.photoType
          }". Era: ${
            photoAnalysis.estimatedEra || 'unspecified'
          }.${coverageNote} Please verify gender, apparent age, source, and medium. When ready, click "Approve & Restore" in the console (or type custom instructions).`
        : `Analysis complete. Source: ${
            photoAnalysis.sourceTypeLabel || photoAnalysis.sourceType || 'unknown'
          }. Medium: "${
            photoAnalysis.photoTypeLabel || photoAnalysis.photoType
          }". Era: ${
            photoAnalysis.estimatedEra || 'unspecified'
          }.${coverageNote} Please verify gender, apparent age, source, and medium. When ready, click "Approve & Restore" in the console (or type custom instructions).`;
      setQueue((prev) =>
        prev.map((q) =>
          q.id === item.id
            ? {
                ...q,
                jobId: jobId || q.jobId,
                analysis: photoAnalysis,
                analysisRevision: (q.analysisRevision || 0) + 1,
                status: 'ready_for_review',
                errorMessage: null,
                messages: [
                  ...q.messages,
                  {
                    id: `diag-${Date.now()}`,
                    sender: 'agent',
                    text: agentText,
                    timestamp: Date.now(),
                  },
                ],
              }
            : q
        )
      );
    } catch (err: unknown) {
      console.error(`Error analyzing item ${item.id}:`, err);
      const msg = formatErrorForChat(err, 'Analysis');
      setErrorMessage(msg);
      setQueue((prev) =>
        prev.map((q) =>
          q.id === item.id
            ? {
                ...q,
                status: 'error',
                errorMessage: msg,
                messages: [
                  ...q.messages,
                  {
                    id: `err-${Date.now()}`,
                    sender: 'agent',
                    text: msg,
                    timestamp: Date.now(),
                  },
                ],
              }
            : q
        )
      );
    }
  };

  // Bulk add multiple files to the restoration queue
  const handleAddFilesToQueue = useCallback(async (files: FileList | null | File[]) => {
    if (!files || files.length === 0) return;
    setErrorMessage(null);

    const newItems: BatchPhotoItem[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) continue;

      try {
        const original = await readOriginalImage(file, file.name);
        const preview = await processAndNormalizeImage(file, file.name, 1600);
        const itemId = `photo-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
        const newItem: BatchPhotoItem = {
          id: itemId,
          fileName: file.name,
          originalImage: original,
          sourceImage: original,
          rawSourceImage: original,
          previewImage: preview,
          jobId: null,
          rotation: 0,
          isCropped: false,
          cropCorners: null,
          damageMask: null,
          maskRegionsSummary: null,
          status: 'idle',
          isAnalysisConfirmed: false,
          analysis: null,
          analysisRevision: 0,
          lastRenderedAnalysisRevision: 0,
          versions: [],
          messages: [
            {
              id: `msg-${Date.now()}-${i}`,
              sender: 'agent',
              text: `Queued "${file.name}". Preserving full-resolution original and performing forensic pre-restoration inspection...`,
              timestamp: Date.now(),
            },
          ],
          timestamp: Date.now(),
        };
        newItems.push(newItem);
      } catch (err: unknown) {
        console.error('File ingest error:', err);
      }
    }

    if (newItems.length > 0) {
      setQueue((prev) => [...prev, ...newItems]);
      setActiveId((current) => current || newItems[0].id);
      setActiveVersionIndex(0);

      for (const item of newItems) {
        runForensicAnalysisOnly(item);
      }
    }
  }, []);

  // Dropzone Handlers
  const handleDropzoneDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDraggingOverDropzone) setIsDraggingOverDropzone(true);
  };

  const handleDropzoneDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOverDropzone(false);
  };

  const handleDropzoneDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOverDropzone(false);
    setIsWindowDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleAddFilesToQueue(e.dataTransfer.files);
    }
  };

  const handleWindowDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isWindowDragging && e.dataTransfer.types.includes('Files')) {
      setIsWindowDragging(true);
    }
  };

  const handleWindowDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.clientX <= 0 || e.clientY <= 0 || e.clientX >= window.innerWidth || e.clientY >= window.innerHeight) {
      setIsWindowDragging(false);
    }
  };

  const handleWindowDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsWindowDragging(false);
    setIsDraggingOverDropzone(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleAddFilesToQueue(e.dataTransfer.files);
    }
  };

  // Add sample photo to queue
  const handleSelectSample = (sampleId: '1940s-portrait' | '1960s-group') => {
    try {
      setErrorMessage(null);
      const sample = createArchivalSamplePhoto(sampleId);
      const itemId = `sample-${Date.now()}`;
      const sampleItem: BatchPhotoItem = {
        id: itemId,
        fileName: `${sampleId}.jpg`,
        originalImage: sample,
        sourceImage: sample,
        rawSourceImage: sample,
        previewImage: sample,
        jobId: null,
        rotation: 0,
        isCropped: false,
        cropCorners: null,
        damageMask: null,
        maskRegionsSummary: null,
        status: 'idle',
        isAnalysisConfirmed: false,
        analysis: null,
        analysisRevision: 0,
        lastRenderedAnalysisRevision: 0,
        versions: [],
        messages: [
          {
            id: `msg-${Date.now()}`,
            sender: 'agent',
            text: `Archival sample loaded. Performing forensic diagnostic analysis for your confirmation...`,
            timestamp: Date.now(),
          },
        ],
        timestamp: Date.now(),
      };

      setQueue((prev) => [...prev, sampleItem]);
      setActiveId(sampleItem.id);
      setActiveVersionIndex(0);

      runForensicAnalysisOnly(sampleItem);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setErrorMessage(`Failed to load archival photograph: ${message}`);
    }
  };

  // Upright Rotation Handler
  const handleRotateActivePhoto = async (direction: 'cw' | 'ccw') => {
    if (!activeItem || isRotating) return;
    setIsRotating(true);
    setErrorMessage(null);

    const delta = direction === 'cw' ? 90 : -90;
    const newRotation = ((activeItem.rotation + delta) % 360 + 360) % 360;

    try {
      const rotatedSource = await rotateImageSource(activeItem.sourceImage, delta);

      const rotatedVersions = await Promise.all(
        activeItem.versions.map(async (v) => {
          const newUrl = await rotateImageUrl(v.imageUrl, delta);
          return {
            ...v,
            imageUrl: newUrl,
          };
        })
      );

      setQueue((prev) =>
        prev.map((item) => {
          if (item.id !== activeItem.id) return item;
          return {
            ...item,
            rotation: newRotation,
            sourceImage: rotatedSource,
            versions: rotatedVersions,
            messages: [
              ...item.messages,
              {
                id: `rot-${Date.now()}`,
                sender: 'agent',
                text: `Rotated 90° ${direction === 'cw' ? 'clockwise' : 'counter-clockwise'} (cumulative: ${newRotation}°). Orientation updated.`,
                timestamp: Date.now(),
              },
            ],
          };
        })
      );
    } catch (err: unknown) {
      console.error('Rotation error:', err);
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMessage(`Failed to rotate photograph: ${msg}`);
    } finally {
      setIsRotating(false);
    }
  };

  // Apply Crop & Corner-Pinning from Modal
  const handleApplyCropCorners = (croppedImage: ImageSource, corners: QuadCorners) => {
    if (!activeItem) return;

    const hasRestored = activeItem.versions.length > 0;

    if (hasRestored) {
      const versionNum = activeItem.versions.length + 1;
      const selected =
        activeItem.versions[activeVersionIndex] ||
        activeItem.versions[activeItem.versions.length - 1];
      const newVersion: RestorationVersion = {
        id: `v${versionNum}-${Date.now()}`,
        timestamp: Date.now(),
        imageUrl: croppedImage.dataUrl,
        label: `Cropped Revision v${versionNum}`,
        promptNotes: `Non-AI crop of ${selected?.label || 'selected restoration'}`,
        agentCommentary: 'Cropped output revision created without modifying the master restoration or source photograph.',
        kind: 'crop',
      };

      setQueue((prev) =>
        prev.map((item) => {
          if (item.id !== activeItem.id) return item;
          return {
            ...item,
            versions: [...item.versions, newVersion],
            messages: [
              ...item.messages,
              {
                id: `crop-out-${Date.now()}`,
                sender: 'agent',
                text: `Created ${newVersion.label} from the selected restored image. The original upload and prior restoration remain unchanged.`,
                timestamp: Date.now(),
                actionTaken: 'Post-restoration crop (non-AI)',
              },
            ],
          };
        })
      );
      setActiveVersionIndex(activeItem.versions.length);
      return;
    }

    setQueue((prev) =>
      prev.map((item) => {
        if (item.id !== activeItem.id) return item;
        return {
          ...item,
          sourceImage: croppedImage,
          isCropped: true,
          cropCorners: corners,
          messages: [
            ...item.messages,
            {
              id: `crop-${Date.now()}`,
              sender: 'agent',
              text: `Manual perspective pinning applied. Surrounding table borders, fingers, and keystone skew have been sliced off cleanly.`,
              timestamp: Date.now(),
              actionTaken: 'Perspective Unwarp & Boundary Extracted',
            },
          ],
        };
      })
    );
  };

  // Reset Crop to raw upload source
  const handleResetCrop = () => {
    if (!activeItem) return;
    setQueue((prev) =>
      prev.map((item) => {
        if (item.id !== activeItem.id) return item;
        return {
          ...item,
          sourceImage: item.rawSourceImage,
          rotation: 0,
          isCropped: false,
          cropCorners: null,
          messages: [
            ...item.messages,
            {
              id: `reset-crop-${Date.now()}`,
              sender: 'agent',
              text: `Reverted to uncropped original photograph.`,
              timestamp: Date.now(),
            },
          ],
        };
      })
    );
  };

  // Apply Damage Mask
  const handleApplyDamageMask = (maskImage: ImageSource, regionsSummary: string) => {
    if (!activeItem) return;

    setQueue((prev) =>
      prev.map((item) => {
        if (item.id !== activeItem.id) return item;
        return {
          ...item,
          damageMask: maskImage,
          maskRegionsSummary: regionsSummary,
          messages: [
            ...item.messages,
            {
              id: `mask-${Date.now()}`,
              sender: 'agent',
              text: `Damage mask applied over severe defect zones: ${regionsSummary}. Model will focus infilling strictly on these painted regions.`,
              timestamp: Date.now(),
              actionTaken: 'Damage Inpainting Mask Active',
            },
          ],
        };
      })
    );
  };

  const handleClearDamageMask = () => {
    if (!activeItem) return;

    setQueue((prev) =>
      prev.map((item) => {
        if (item.id !== activeItem.id) return item;
        return {
          ...item,
          damageMask: null,
          maskRegionsSummary: null,
          messages: [
            ...item.messages,
            {
              id: `clear-mask-${Date.now()}`,
              sender: 'agent',
              text: `Damage mask removed. General whole-image conservation active.`,
              timestamp: Date.now(),
            },
          ],
        };
      })
    );
  };

  // Manual Photographic Medium Reclassification
  const handleMediumReclassify = (newMedium: PhotoMediumType) => {
    if (!activeItem || !activeItem.analysis) return;
    const label = MEDIUM_LABELS[newMedium] || newMedium;
    const hadRestored = activeItem.versions.length > 0;
    const sourcePatch =
      newMedium === 'digital-photo'
        ? {
            sourceType: 'native-digital' as const,
            sourceTypeLabel: 'Native Digital Photograph',
          }
        : {};

    setQueue((prev) =>
      prev.map((item) => {
        if (item.id !== activeItem.id || !item.analysis) return item;
        const nextRevision = (item.analysisRevision || 0) + 1;
        return {
          ...item,
          analysisRevision: nextRevision,
          analysis: {
            ...item.analysis,
            ...sourcePatch,
            photoType: newMedium,
            photoTypeLabel: label,
            summary: `Medium updated to ${label}. Tone conservation and color balance adjusted accordingly.`,
          },
          messages: [
            ...item.messages,
            {
              id: `medium-update-${Date.now()}`,
              sender: 'agent',
              text: hadRestored
                ? `Medium reclassified to "${label}". The displayed restoration used previous medium settings. Re-render with Updated Settings to apply this change.`
                : `Medium reclassified to "${label}". When confirmed, restoration will strictly respect this palette.`,
              timestamp: Date.now(),
              actionTaken: `Medium set to ${label}`,
            },
          ],
        };
      })
    );
  };

  // Update specific subject's gender, age, or safeguards
  const handleSubjectUpdate = (
    subjectIndex: number,
    updatedFields: Partial<SubjectIdentity>
  ) => {
    if (!activeItem || !activeItem.analysis) return;

    setQueue((prev) =>
      prev.map((item) => {
        if (item.id !== activeItem.id || !item.analysis) return item;

        const currentSubjects = item.analysis.subjects || [];
        const updatedSubjects = currentSubjects.map((s) => {
          if (s.index !== subjectIndex) return s;
          return {
            ...s,
            ...updatedFields,
          };
        });

        const targetSubject = updatedSubjects.find((s) => s.index === subjectIndex);
        const locator = targetSubject?.visualLocator ? ` (${targetSubject.visualLocator})` : '';
        const changeDesc = updatedFields.perceivedGender
          ? `Person #${subjectIndex}${locator} gender updated to "${updatedFields.perceivedGender}"`
          : `Person #${subjectIndex}${locator} was updated to ${updatedFields.apparentAge}`;
        const hadRestored = item.versions.length > 0;
        const nextRevision = (item.analysisRevision || 0) + 1;

        return {
          ...item,
          analysisRevision: nextRevision,
          analysis: {
            ...item.analysis,
            subjects: updatedSubjects,
          },
          messages: [
            ...item.messages,
            {
              id: `subj-update-${Date.now()}`,
              sender: 'agent',
              text: hadRestored
                ? `${changeDesc}.

The displayed restoration was generated using the previous subject settings.

Re-render to apply the updated identity guidance.`
                : `${changeDesc}. Safeguards updated to lock this identity: "${targetSubject?.genderSafeguards || ''}". Click "Approve & Restore" in the console to execute.`,
              timestamp: Date.now(),
              actionTaken: 'Identity Alignment Adjusted',
            },
          ],
        };
      })
    );
  };

  // Add a new subject manually (no map marker)
  const handleAddSubject = () => {
    if (!activeItem || !activeItem.analysis) return;
    setSubjectPlacementMode(false);
    setLocatingPersonIndex(null);

    const currentSubjects = activeItem.analysis.subjects || [];
    const newIndex =
      currentSubjects.reduce((max, s) => Math.max(max, Number(s.index) || 0), 0) + 1;
    const newSubject: SubjectIdentity = {
      index: newIndex,
      perceivedGender: 'female',
      apparentAge: 'Adult woman',
      facialStructure: 'Preserve natural facial features and bone structure.',
      hairAndStyling: 'Period hairstyle, neckline.',
      genderSafeguards: 'NEVER alter gender. Retain female facial anatomy; do NOT square the jaw, do NOT add stubble.',
      facialSoftnessGuidance: 'Keep skin smooth and analog. Do NOT carve deep smile creases or harsh lines.',
      visualLocator: 'User-added subject (position not mapped)',
      visualDescription: 'Manually added person — set gender and age, then describe position if helpful.',
      isUserOverridden: true,
    };

    setQueue((prev) =>
      prev.map((item) => {
        if (item.id !== activeItem.id || !item.analysis) return item;
        const subjects = [...currentSubjects, newSubject];
        const mappedFaceCount = subjects.filter(
          (s) => s.faceBounds && s.faceBoundsSource !== 'legacy'
        ).length;
        return {
          ...item,
          analysisRevision: (item.analysisRevision || 0) + 1,
          analysis: {
            ...item.analysis,
            subjects,
            visiblePersonCount: subjects.length,
            mappedFaceCount,
            unmappedSubjectCount: Math.max(0, subjects.length - mappedFaceCount),
          },
          messages: [
            ...item.messages,
            {
              id: `subj-add-${Date.now()}`,
              sender: 'agent',
              text:
                item.versions.length > 0
                  ? `Added Person #${newIndex}. Re-render with Updated Settings to include them in a new restoration revision.`
                  : `Added Person #${newIndex} to identity alignment list. Adjust their details then click "Approve & Restore" to proceed.`,
              timestamp: Date.now(),
              actionTaken: `Person #${newIndex} Added`,
            },
          ],
        };
      })
    );
  };

  const handlePlaceSubjectFace = (nx: number, ny: number) => {
    if (!activeItem || !activeItem.analysis) return;
    const currentSubjects = activeItem.analysis.subjects || [];
    const bounds = faceBoundsFromClick(nx, ny, 0.09);
    const targetIndex = locatingPersonIndex;

    // Prefer the person being located/adjusted; else first unmapped; else create new
    const existingTarget =
      (targetIndex != null
        ? currentSubjects.find((s) => s.index === targetIndex)
        : null) ||
      currentSubjects.find((s) => !s.faceBounds || s.faceBoundsSource === 'legacy');

    const wasAlreadyMapped = Boolean(
      existingTarget &&
        existingTarget.faceBounds &&
        existingTarget.faceBoundsSource !== 'legacy'
    );

    setSubjectPlacementMode(false);
    setLocatingPersonIndex(null);

    setQueue((prev) =>
      prev.map((item) => {
        if (item.id !== activeItem.id || !item.analysis) return item;
        let subjects: SubjectIdentity[];
        let newIndex: number;
        let action: string;
        let messageText: string;
        let locationOnly = false;

        if (existingTarget) {
          newIndex = existingTarget.index;
          locationOnly = true;
          subjects = currentSubjects.map((s) =>
            s.index === existingTarget.index
              ? {
                  ...s,
                  faceBounds: bounds,
                  faceBoundsSource: 'user' as const,
                  // Location-only: do not flip identity override / description fields
                }
              : s
          );
          action = wasAlreadyMapped
            ? `Person #${newIndex} location updated`
            : `Person #${newIndex} located on photo`;
          messageText = wasAlreadyMapped
            ? `Person #${newIndex} location updated.`
            : `Located Person #${newIndex} on the photo.`;
        } else {
          newIndex =
            currentSubjects.reduce((max, s) => Math.max(max, Number(s.index) || 0), 0) + 1;
          subjects = [
            ...currentSubjects,
            {
              index: newIndex,
              perceivedGender: 'unspecified',
              apparentAge: 'Unknown',
              facialStructure: 'Preserve natural facial features and bone structure.',
              hairAndStyling: 'Period hairstyle.',
              genderSafeguards:
                'Strictly preserve original facial likeness without artificial gender bias.',
              facialSoftnessGuidance: 'Maintain smooth analog optical softness.',
              visualLocator: 'User-mapped on Subject Map',
              visualDescription:
                'Manually placed face marker — set gender and apparent age before restoring.',
              faceBounds: bounds,
              faceBoundsSource: 'user',
              isUserOverridden: true,
            },
          ];
          action = `Person #${newIndex} Added & Mapped`;
          messageText = `Added Person #${newIndex} from your click on the photo. Set gender and age if needed.`;
        }

        const mappedFaceCount = subjects.filter(
          (s) => s.faceBounds && s.faceBoundsSource !== 'legacy'
        ).length;

        return {
          ...item,
          // Location-only edits must not mark restoration identity settings dirty
          analysisRevision: locationOnly
            ? item.analysisRevision
            : (item.analysisRevision || 0) + 1,
          analysis: {
            ...item.analysis,
            subjects,
            visiblePersonCount: subjects.length,
            mappedFaceCount,
            unmappedSubjectCount: Math.max(0, subjects.length - mappedFaceCount),
          },
          messages: [
            ...item.messages,
            {
              id: `subj-place-${Date.now()}`,
              sender: 'agent',
              text: messageText,
              timestamp: Date.now(),
              actionTaken: action,
            },
          ],
        };
      })
    );
  };

  // Remove a subject
  const handleRemoveSubject = (subjectIndex: number) => {
    if (!activeItem || !activeItem.analysis) return;

    const currentSubjects = activeItem.analysis.subjects || [];
    const filtered = currentSubjects
      .filter((s) => s.index !== subjectIndex)
      .map((s, idx) => ({ ...s, index: idx + 1 }));

    setQueue((prev) =>
      prev.map((item) => {
        if (item.id !== activeItem.id || !item.analysis) return item;
        return {
          ...item,
          analysisRevision: (item.analysisRevision || 0) + 1,
          analysis: {
            ...item.analysis,
            subjects: filtered,
          },
          messages: [
            ...item.messages,
            {
              id: `subj-rem-${Date.now()}`,
              sender: 'agent',
              text:
                item.versions.length > 0
                  ? `Removed Person #${subjectIndex}. Re-render with Updated Settings if you want a new restoration without that subject entry.`
                  : `Removed Person #${subjectIndex} from active subject list.`,
              timestamp: Date.now(),
            },
          ],
        };
      })
    );
  };

  // Execute Restoration for a single Queue Item AFTER user approval
  const executeRestorationForItem = async (
    item: BatchPhotoItem,
    customInstruction?: string,
    referenceImage?: ImageSource,
    options?: { isIdentityRerender?: boolean }
  ): Promise<void> => {
    setErrorMessage(null);
    const isIdentityRerender = Boolean(options?.isIdentityRerender);
    const hasPriorVersions = item.versions.length > 0;
    const treatAsTweak = isIdentityRerender || hasPriorVersions;
    const renderedRevision = item.analysisRevision || 0;

    let photoAnalysis = item.analysis;
    if (!photoAnalysis) {
      setQueue((prev) =>
        prev.map((q) => (q.id === item.id ? { ...q, status: 'analyzing', errorMessage: null } : q))
      );
      try {
        let detectedFaces: DetectedFace[] = [];
        try {
          detectedFaces = await detectFacesForAnalysis(item.previewImage.dataUrl);
        } catch {
          detectedFaces = [];
        }
        let subjectMap: ImageSource | null = null;
        if (detectedFaces.length > 0) {
          try {
            subjectMap = await createAnnotatedSubjectMap(item.previewImage, detectedFaces);
          } catch {
            subjectMap = null;
          }
        }
        const result = await analyzePhotograph(item.previewImage, item.originalImage, {
          cacheKey: item.id,
          subjectMap,
          detectedFaces,
          previewUrl: item.previewImage.dataUrl,
        });
        photoAnalysis = result.analysis;
        if (result.jobId) {
          setQueue((prev) =>
            prev.map((q) => (q.id === item.id ? { ...q, jobId: result.jobId } : q))
          );
        }
        if (result.warning) {
          setQueue((prev) =>
            prev.map((q) =>
              q.id === item.id
                ? {
                    ...q,
                    messages: [
                      ...q.messages,
                      {
                        id: `warn-${Date.now()}`,
                        sender: 'agent',
                        text: `Analysis warning: ${result.warning}`,
                        timestamp: Date.now(),
                      },
                    ],
                  }
                : q
            )
          );
        }
      } catch (err: unknown) {
        console.error('Analysis error:', err);
        const msg = formatErrorForChat(err, 'Analysis');
        setErrorMessage(msg);
        setQueue((prev) =>
          prev.map((q) =>
            q.id === item.id
              ? {
                  ...q,
                  status: 'error',
                  errorMessage: msg,
                  messages: [
                    ...q.messages,
                    {
                      id: `err-${Date.now()}`,
                      sender: 'agent',
                      text: msg,
                      timestamp: Date.now(),
                    },
                  ],
                }
              : q
          )
        );
        return;
      }
    }

    const initialNoteMessage = isIdentityRerender
      ? `Re-rendering with updated identity settings (gender/age/medium). Using original source photograph — not the prior AI output.`
      : customInstruction
      ? `Configuration confirmed with note: "${customInstruction}". Executing restoration with locked sex/gender fidelity (${
          photoAnalysis?.subjects?.map((s) => `#${s.index} ${s.perceivedGender}`).join(', ') || 'verified'
        })${item.damageMask ? ' and inpainting damage mask.' : '.'}`
      : `Configuration confirmed! Executing restoration for "${item.fileName}" with locked sex/gender fidelity (${
          photoAnalysis?.subjects?.map((s) => `#${s.index} ${s.perceivedGender}`).join(', ') || 'verified'
        })${item.damageMask ? ' and inpainting damage mask.' : '.'}`;

    setQueue((prev) =>
      prev.map((q) =>
        q.id === item.id
          ? {
              ...q,
              analysis: photoAnalysis,
              status: 'restoring',
              isAnalysisConfirmed: true,
              errorMessage: null,
              messages: [
                ...q.messages,
                {
                  id: `restoring-${Date.now()}`,
                  sender: 'agent',
                  text: initialNoteMessage,
                  timestamp: Date.now(),
                },
              ],
            }
          : q
      )
    );

    try {
      // Highest-quality working source: cropped/rotated sourceImage, else untouched original
      const restoreSource =
        item.isCropped || item.rotation ? item.sourceImage : item.originalImage;

      const identityInstruction =
        isIdentityRerender
          ? customInstruction ||
            'Re-render using the corrected subject identity settings (gender, apparent age, and safeguards). Preserve likeness; apply updated identity locks only.'
          : customInstruction;

      const restorationResult = await restorePhotograph(
        restoreSource,
        photoAnalysis,
        identityInstruction,
        referenceImage,
        item.damageMask || undefined,
        item.maskRegionsSummary,
        { isTweak: treatAsTweak, jobId: item.jobId, cacheKey: item.id }
      );

      const versionNum = item.versions.length + 1;
      const newVersion: RestorationVersion = {
        id: `v${versionNum}-${Date.now()}`,
        timestamp: Date.now(),
        imageUrl: restorationResult.imageUrl,
        label:
          versionNum === 1
            ? 'Master Restoration v1'
            : isIdentityRerender
              ? `Identity Revision v${versionNum}`
              : `Revision v${versionNum}`,
        promptNotes:
          identityInstruction ||
          'Restoration executed with confirmed user identity & gender configuration.',
        agentCommentary: restorationResult.commentary,
        kind: 'ai',
      };

      setQueue((prev) =>
        prev.map((q) =>
          q.id === item.id
            ? {
                ...q,
                jobId: restorationResult.jobId || q.jobId,
                status: 'completed',
                isAnalysisConfirmed: true,
                lastRenderedAnalysisRevision: renderedRevision,
                versions: [...q.versions, newVersion],
                messages: [
                  ...q.messages,
                  {
                    id: `done-${Date.now()}`,
                    sender: 'agent',
                    text: isIdentityRerender
                      ? `Identity revision complete. ${newVersion.label} now uses the updated subject settings. Prior revisions remain available.`
                      : `Restoration complete for "${item.fileName}". Facial identity, verified gender traits, and authentic analog skin softness have been preserved.`,
                    timestamp: Date.now(),
                    actionTaken: `${newVersion.label} Delivered`,
                  },
                ],
              }
            : q
        )
      );
      setActiveVersionIndex(item.versions.length);
      await refreshBalance();
      if (restorationResult.warning) {
        setQueue((prev) =>
          prev.map((q) =>
            q.id === item.id
              ? {
                  ...q,
                  messages: [
                    ...q.messages,
                    {
                      id: `warn-${Date.now()}`,
                      sender: 'agent',
                      text: `Note: ${restorationResult.warning}`,
                      timestamp: Date.now(),
                    },
                  ],
                }
              : q
          )
        );
      }
    } catch (err: unknown) {
      console.error(`Error restoring item ${item.id}:`, err);
      const msg = formatErrorForChat(err, 'Restoration');
      setErrorMessage(msg);
      setQueue((prev) =>
        prev.map((q) =>
          q.id === item.id
            ? {
                ...q,
                status: 'error',
                errorMessage: msg,
                messages: [
                  ...q.messages,
                  {
                    id: `err-${Date.now()}`,
                    sender: 'agent',
                    text: msg,
                    timestamp: Date.now(),
                  },
                ],
              }
            : q
        )
      );
    }
  };

  // Batch process all ready/idle items sequentially
  const handleProcessEntireBatch = async () => {
    if (isProcessingBatch) return;
    setIsProcessingBatch(true);

    const targetItems = queue.filter(
      (item) => item.status === 'ready_for_review' || item.status === 'idle' || item.status === 'error'
    );

    for (const item of targetItems) {
      setActiveId(item.id);
      await executeRestorationForItem(item);
    }

    setIsProcessingBatch(false);
  };

  // Bulk ZIP Download
  const handleDownloadAllZip = async () => {
    const completedItems = queue.filter(
      (item) => item.status === 'completed' && item.versions.length > 0
    );

    if (completedItems.length === 0 || isZipping) return;

    setIsZipping(true);
    setErrorMessage(null);

    try {
      const usedNames = new Set<string>();
      const filesToZip: Array<{ name: string; data: Uint8Array }> = [];

      for (let i = 0; i < completedItems.length; i++) {
        const item = completedItems[i];
        const latestVersion = item.versions[item.versions.length - 1];

        const baseName = item.fileName.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9_\-]/g, '_');
        let finalFileName = `restored_${baseName}.png`;
        let counter = 1;
        while (usedNames.has(finalFileName)) {
          finalFileName = `restored_${baseName}_(${counter}).png`;
          counter++;
        }
        usedNames.add(finalFileName);

        const exportUrl = watermarkExportsEnabled
          ? await createWatermarkedImage(latestVersion.imageUrl)
          : latestVersion.imageUrl;
        const dataBytes = dataUrlToUint8Array(exportUrl);
        filesToZip.push({
          name: finalFileName,
          data: dataBytes,
        });
      }

      const zipBlob = createZipBlob(filesToZip);
      const timestamp = new Date().toISOString().slice(0, 10);
      const downloadUrl = URL.createObjectURL(zipBlob);
      const downloadLink = document.createElement('a');
      downloadLink.href = downloadUrl;
      downloadLink.download = `restored_family_photos_${timestamp}.zip`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadUrl);
    } catch (err: unknown) {
      console.error('Failed to create ZIP archive:', err);
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMessage(`Failed to create ZIP archive: ${msg}`);
    } finally {
      setIsZipping(false);
    }
  };

  // Follow-up refinement on the active photo
  const handleFollowUpInstruction = async (instruction: string, referenceImage?: ImageSource) => {
    if (!activeItem || !activeItem.analysis) return;

    setErrorMessage(null);
    setQueue((prev) =>
      prev.map((q) =>
        q.id === activeItem.id
          ? {
              ...q,
              status: 'restoring',
              messages: [
                ...q.messages,
                {
                  id: `usr-${Date.now()}`,
                  sender: 'user',
                  text: instruction,
                  timestamp: Date.now(),
                  referencedImage: referenceImage?.fileName,
                },
              ],
            }
          : q
      )
    );

    try {
      const restoreSource =
        activeItem.isCropped || activeItem.rotation
          ? activeItem.sourceImage
          : activeItem.originalImage;

      const result = await restorePhotograph(
        restoreSource,
        activeItem.analysis,
        instruction,
        referenceImage,
        activeItem.damageMask || undefined,
        activeItem.maskRegionsSummary,
        { isTweak: true, jobId: activeItem.jobId, cacheKey: activeItem.id }
      );

      const newVersionNum = activeItem.versions.length + 1;
      const newVersion: RestorationVersion = {
        id: `v${newVersionNum}-${Date.now()}`,
        timestamp: Date.now(),
        imageUrl: result.imageUrl,
        label: `Revision v${newVersionNum}`,
        promptNotes: instruction,
        agentCommentary: result.commentary,
      };

      setQueue((prev) =>
        prev.map((q) => {
          if (q.id !== activeItem.id) return q;
          return {
            ...q,
            status: 'completed',
            lastRenderedAnalysisRevision: q.analysisRevision || 0,
            versions: [...q.versions, newVersion],
            messages: [
              ...q.messages,
              {
                id: `agent-${Date.now()}`,
                sender: 'agent',
                text:
                  result.commentary ||
                  `Adjustment applied: "${instruction}". Facial integrity and historical character maintained.`,
                timestamp: Date.now(),
                actionTaken: `Updated to Revision v${newVersionNum}`,
              },
            ],
          };
        })
      );
      setActiveVersionIndex(activeItem.versions.length);
      await refreshBalance();
      if (result.warning) {
        setQueue((prev) =>
          prev.map((q) =>
            q.id === activeItem.id
              ? {
                  ...q,
                  messages: [
                    ...q.messages,
                    {
                      id: `warn-${Date.now()}`,
                      sender: 'agent',
                      text: `Note: ${result.warning}`,
                      timestamp: Date.now(),
                    },
                  ],
                }
              : q
          )
        );
      }
    } catch (err: unknown) {
      console.error(err);
      const msg = formatErrorForChat(err, 'Refinement');
      setErrorMessage(msg);
      setQueue((prev) =>
        prev.map((q) =>
          q.id === activeItem.id
            ? {
                ...q,
                status: 'error',
                errorMessage: msg,
                messages: [
                  ...q.messages,
                  {
                    id: `agent-err-${Date.now()}`,
                    sender: 'agent',
                    text: msg,
                    timestamp: Date.now(),
                  },
                ],
              }
            : q
        )
      );
    }
  };

  const handleRemovePhotoFromQueue = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setQueue((prev) => {
      const filtered = prev.filter((item) => item.id !== id);
      if (activeId === id) {
        setActiveId(filtered.length > 0 ? filtered[0].id : null);
        setActiveVersionIndex(0);
      }
      return filtered;
    });
  };

  const handleRerenderWithUpdatedSettings = async () => {
    if (!activeItem || !activeItem.analysis || activeItem.versions.length === 0) return;
    await executeRestorationForItem(activeItem, undefined, undefined, {
      isIdentityRerender: true,
    });
  };

  const resolveExportImageUrl = async (masterImageUrl: string): Promise<string> => {
    if (!watermarkExportsEnabled) return masterImageUrl;
    return createWatermarkedImage(masterImageUrl);
  };

  const handleDownloadActiveImage = async () => {
    if (!activeItem || activeItem.versions.length === 0) return;
    const upscaled =
      activeUpscaleId &&
      (activeItem.upscaledAssets || []).find((a) => a.id === activeUpscaleId);
    const version =
      activeItem.versions[activeVersionIndex] ||
      activeItem.versions[activeItem.versions.length - 1];
    const masterUrl = upscaled?.imageUrl || version.imageUrl;
    if (!masterUrl) return;

    const exportUrl = await resolveExportImageUrl(masterUrl);
    const base = activeItem.fileName.replace(/\.[^/.]+$/, '') || 'photo';
    const link = document.createElement('a');
    link.href = exportUrl;
    link.download = upscaled
      ? `kinvera-restored-${base}-upscaled-${upscaled.factor}x.png`
      : `kinvera-restored-${base}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const dataUrlToImagePayload = (dataUrl: string) => {
    const match = String(dataUrl || '').match(/^data:([^;]+);base64,(.+)$/);
    if (!match) throw new Error('Selected restoration image is not available as image data.');
    return { mimeType: match[1] || 'image/png', base64Data: match[2] };
  };

  const handleConfirmUpscale = async (factor: number) => {
    if (!activeItem?.jobId || isUpscaling) return;
    const version =
      activeItem.versions[activeVersionIndex] ||
      activeItem.versions[activeItem.versions.length - 1];
    if (!version?.imageUrl) return;

    setIsUpscaling(true);
    setUpscaleError(null);
    setUpscaleStatus(`Upscaling ${factor}×...`);

    try {
      const measured = await measureImageResolution(version.imageUrl);
      const image = dataUrlToImagePayload(version.imageUrl);
      const started = await api.upscaleJob(activeItem.jobId, {
        sourceVersionId: version.id,
        sourceVersionLabel: version.label,
        image,
        factor,
        inputWidth: measured.width,
        inputHeight: measured.height,
      });

      const pollStarted = Date.now();
      let op = await api.upscaleOperation(started.operationId, token);
      while (op.status === 'processing' && Date.now() - pollStarted < 10 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 2000));
        op = await api.upscaleOperation(started.operationId, token);
      }

      if (op.status === 'failed') {
        throw new KinveraApiError(op.error?.message || 'Upscale failed. Your photo is unchanged.', {
          code: op.error?.code || 'UPSCALE_FAILED',
          diagnostics: op.diagnostics || null,
        });
      }
      if (op.status !== 'completed' || !op.imageUrl) {
        throw new KinveraApiError('Upscale is taking longer than expected. Please try again shortly.', {
          code: 'UPSCALE_TIMEOUT',
          retryable: true,
        });
      }

      const asset: UpscaledAsset = {
        id: String(op.id),
        sourceVersionId: version.id,
        sourceVersionLabel: version.label,
        factor: Number(op.factor),
        inputWidth: Number(op.inputWidth || measured.width),
        inputHeight: Number(op.inputHeight || measured.height),
        outputWidth: Number(op.outputWidth || measured.width * factor),
        outputHeight: Number(op.outputHeight || measured.height * factor),
        imageUrl: op.imageUrl,
        storagePath: null,
        provider: 'leonardo',
        createdAt: new Date().toISOString(),
      };

      setQueue((prev) =>
        prev.map((q) =>
          q.id === activeItem.id
            ? { ...q, upscaledAssets: [...(q.upscaledAssets || []), asset] }
            : q
        )
      );
      setActiveUpscaleId(asset.id);
      setIsUpscaleOpen(false);
      setUpscaleStatus(`${factor}× upscale complete`);
      void refreshBalance();
    } catch (err) {
      // Never surface purchase/credit paywall copy during Alpha free modes
      const apiErr = err instanceof KinveraApiError ? err : null;
      let msg =
        apiErr?.message ||
        (err instanceof Error ? err.message : 'Upscale failed. Your photo is unchanged.');
      if (apiErr?.detail) {
        msg = `${msg}\n\nCode: ${apiErr.code || 'UPSCALE_FAILED'}\nDetail: ${apiErr.detail}`;
      }
      if (
        !productConfig?.creditsEnforced &&
        productConfig?.creditMode !== 'enforced' &&
        apiErr?.code === 'INSUFFICIENT_CREDITS'
      ) {
        msg = 'Upscaling failed. Your photo is unchanged.';
      }
      setUpscaleError(msg);
      setUpscaleStatus(null);
    } finally {
      setIsUpscaling(false);
    }
  };

  const isCurrentProcessing =
    activeItem?.status === 'analyzing' || activeItem?.status === 'restoring';

  const analysisDirty = Boolean(
    activeItem &&
      activeItem.versions.length > 0 &&
      (activeItem.analysisRevision || 0) > (activeItem.lastRenderedAnalysisRevision || 0)
  );

  const cropModalSource: ImageSource | null = activeItem
    ? activeItem.versions.length > 0
      ? (() => {
          try {
            const ver =
              activeItem.versions[activeVersionIndex] ||
              activeItem.versions[activeItem.versions.length - 1];
            return imageSourceFromDataUrl(
              ver.imageUrl,
              `restored-${activeItem.fileName}`
            );
          } catch {
            return activeItem.sourceImage;
          }
        })()
      : activeItem.sourceImage
    : null;

  const cropEditingLabel =
    activeItem && activeItem.versions.length > 0
      ? `Editing: ${
          activeItem.versions[activeVersionIndex]?.label ||
          activeItem.versions[activeItem.versions.length - 1]?.label
        }`
      : undefined;

  return (
    <div
      onDragOver={handleWindowDragOver}
      onDragLeave={handleWindowDragLeave}
      onDrop={handleWindowDrop}
      className="kin-studio-wrap min-h-screen flex flex-col font-sans relative"
    >
      {/* Full-Window Drag & Drop Overlay */}
      {isWindowDragging && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-md border-4 border-dashed border-teal-500 flex flex-col items-center justify-center p-6 pointer-events-none transition-all animate-in fade-in">
          <div className="w-20 h-20 rounded-3xl bg-teal-500/20 border border-teal-500/50 flex items-center justify-center text-teal-200 mb-4 animate-bounce">
            <FileImage className="w-10 h-10" />
          </div>
          <h3 className="text-2xl font-bold text-white">Drop your family photos here</h3>
          <p className="text-sm text-slate-200 mt-2 max-w-md text-center">
            Queue single or batch photographs for archival damage repair, perspective squaring, and identity preservation.
          </p>
        </div>
      )}

      {/* Top Banner Navigation */}
      <header className="kin-studio-header sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between gap-2">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
            <BrandLogo to="/" size="lg" className="shrink-0 kin-studio-brand-logo" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-base font-bold text-slate-900 tracking-tight">
                  Studio
                </h1>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500 border border-slate-200">
                  Alpha
                </span>
              </div>
              <p className="text-xs text-slate-600 truncate hidden sm:block">
                Restore the photo. Preserve the person.
              </p>
            </div>
          </div>

          <div className="flex items-center flex-wrap justify-end gap-2 sm:gap-3 text-xs text-slate-600">
            <span
              className="hidden sm:inline px-2 py-0.5 rounded border border-teal-200 bg-teal-50 text-teal-800"
              title="Restorations are free while we test and improve the service"
            >
              {stageLabel}
              {restorationsFree ? ' · Free access' : ''}
            </span>
            <Link
              to="/app/library"
              className="inline-flex items-center min-h-10 px-2 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 rounded"
            >
              Library
            </Link>
            {!restorationsFree && balance && (
              <Link to="/app/account#billing" className="inline-flex items-center min-h-10 px-2 text-teal-700 hover:text-teal-900">
                {balance.credits} credits
                {balance.freeRestorationsRemaining
                  ? ` · ${balance.freeRestorationsRemaining} free`
                  : ''}
              </Link>
            )}
            {queue.length > 0 && (
              <span className="hidden sm:inline font-mono text-slate-500">
                {queue.filter((q) => q.status === 'completed').length} / {queue.length} Restored
              </span>
            )}
            {user && <AccountMenu variant="studio" />}
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex-1 w-full flex flex-col space-y-6">
        {/* Error notification alert */}
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3.5 rounded-xl flex items-start space-x-3 text-xs shadow-sm">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <span className="font-semibold block text-red-900">Notice from Restoration Agent:</span>
              <p className="leading-relaxed">{errorMessage}</p>
            </div>
            <button
              onClick={() => setErrorMessage(null)}
              className="text-slate-500 hover:text-slate-800 text-sm font-bold ml-2"
            >
              ✕
            </button>
          </div>
        )}

        {/* Empty state / Welcome onboarding if no photos are queued yet */}
        {queue.length === 0 && (
          <div className="flex flex-col items-center justify-center py-6 px-4 text-center max-w-3xl mx-auto space-y-6">
            <div className="p-4 sm:p-7 kin-studio-panel shadow-sm w-full">
              <div className="w-14 h-14 rounded-2xl bg-teal-500/10 border border-teal-500/30 flex items-center justify-center mx-auto text-teal-600 mb-4">
                <Camera className="w-7 h-7" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                Restore old physical family photographs
              </h2>
              <p className="text-xs sm:text-sm text-slate-600 mt-2 max-w-lg mx-auto leading-relaxed">
                Snap a picture of an old album photo, framed portrait, or creased print.
                Kinvera analyzes the capture, un-distorts perspective, removes glare,
                heals scratches, and preserves authentic facial identity without artificial smoothing.
              </p>

              <div className="mt-4 flex justify-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setShowGooglePhotos((v) => !v)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:border-teal-500"
                >
                  {showGooglePhotos ? 'Hide Google Photos' : 'Import from Google Photos'}
                </button>
                {!restorationsFree && (
                  <Link
                    to="/pricing"
                    className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 text-teal-700 hover:border-teal-500"
                  >
                    Get credits
                  </Link>
                )}
                {restorationsFree && (
                  <span className="text-xs px-3 py-1.5 rounded-lg border border-teal-200 text-teal-800 bg-teal-50">
                    Restorations currently free while we test Kinvera
                  </span>
                )}
              </div>
              {showGooglePhotos && (
                <GooglePhotosPanel
                  onImport={async (image) => {
                    const itemId = `gphotos-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
                    const source: ImageSource = {
                      dataUrl: image.dataUrl,
                      base64Data: image.base64Data,
                      mimeType: image.mimeType,
                      fileName: image.fileName,
                    };
                    let preview = source;
                    try {
                      const blob = await (await fetch(image.dataUrl)).blob();
                      preview = await processAndNormalizeImage(blob, image.fileName, 1600);
                    } catch {
                      /* keep full image as preview fallback */
                    }
                    const newItem: BatchPhotoItem = {
                      id: itemId,
                      fileName: image.fileName,
                      originalImage: source,
                      sourceImage: source,
                      rawSourceImage: source,
                      previewImage: preview,
                      jobId: null,
                      rotation: 0,
                      isCropped: false,
                      cropCorners: null,
                      damageMask: null,
                      maskRegionsSummary: null,
                      status: 'idle',
                      isAnalysisConfirmed: false,
                      analysis: null,
                      analysisRevision: 0,
                      lastRenderedAnalysisRevision: 0,
                      versions: [],
                      messages: [
                        {
                          id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                          sender: 'agent',
                          text: `Imported "${image.fileName}" from Google Photos. Preserving original and performing forensic inspection...`,
                          timestamp: Date.now(),
                        },
                      ],
                      timestamp: Date.now(),
                    };
                    setQueue((prev) => [...prev, newItem]);
                    setActiveId(itemId);
                    runForensicAnalysisOnly(newItem);
                  }}
                />
              )}

              {/* DEDICATED DRAG AND DROP ZONE */}
              <div
                onDragOver={handleDropzoneDragOver}
                onDragLeave={handleDropzoneDragLeave}
                onDrop={handleDropzoneDrop}
                className={`mt-6 border-2 border-dashed rounded-2xl p-8 sm:p-10 transition-all duration-200 relative group cursor-pointer ${
                  isDraggingOverDropzone
                    ? 'border-teal-400 bg-teal-950/30 scale-[1.01] shadow-xl shadow-teal-950/30'
                    : 'border-slate-300 hover:border-teal-400 bg-slate-50 hover:bg-white'
                }`}
              >
                <input
                  type="file"
                  id="main-dropzone-file-upload"
                  multiple
                  accept="image/*"
                  onChange={(e) => {
                    handleAddFilesToQueue(e.target.files);
                    e.target.value = '';
                  }}
                  className="hidden"
                />

                <label
                  htmlFor="main-dropzone-file-upload"
                  className="cursor-pointer flex flex-col items-center space-y-3.5 select-none"
                >
                  <div
                    className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                      isDraggingOverDropzone
                        ? 'bg-teal-500 text-stone-950 scale-110 shadow-lg'
                        : 'bg-kinvera-navy group-hover:bg-teal-700 text-white border border-kinvera-navy-dark group-hover:border-teal-500'
                    }`}
                  >
                    <FileImage className="w-7 h-7" />
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm sm:text-base font-semibold text-[color:var(--kinvera-text)] transition-colors">
                      {isDraggingOverDropzone ? (
                        <span className="text-teal-600 font-bold">Release to drop photograph(s) here</span>
                      ) : (
                        <>
                          <span className="text-teal-600 underline underline-offset-4 decoration-teal-500/60">
                            Click to select photo(s)
                          </span>{' '}
                          <span className="text-[color:var(--kinvera-text)]">
                            or drag & drop one or multiple photos here
                          </span>
                        </>
                      )}
                    </p>
                    <p className="text-xs text-slate-600">
                      Supports multiple batch uploads (JPEG, PNG, WebP)
                    </p>
                  </div>

                  <div className="kin-feature-strip">
                    <span className="kin-feature-item">
                      <Layers aria-hidden />
                      Batch restoration
                    </span>
                    <span className="kin-feature-item">
                      <Paintbrush aria-hidden />
                      Targeted damage repair
                    </span>
                    <span className="kin-feature-item">
                      <ShieldCheck aria-hidden />
                      Identity safeguards
                    </span>
                  </div>
                </label>
              </div>
            </div>

            {/* Quick Test Samples */}
            <div className="w-full">
              <div className="flex items-center justify-center space-x-2 text-xs text-slate-600 mb-3">
                <Sparkles className="w-3.5 h-3.5 text-teal-600" />
                <span>Or test immediately with an archival damaged photograph sample:</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
                {ARCHIVAL_SAMPLES.map((sample) => (
                  <div
                    key={sample.id}
                    onClick={() => handleSelectSample(sample.id)}
                    className="p-3.5 bg-white border border-slate-200 hover:border-teal-500 rounded-xl cursor-pointer text-left transition-all hover:bg-slate-50 flex items-center space-x-3 group shadow-sm"
                  >
                    <div className="w-12 h-12 rounded-lg bg-kinvera-navy border border-kinvera-navy-dark flex items-center justify-center text-white font-bold text-xs shrink-0">
                      {sample.id === '1940s-portrait' ? '1940' : '1960'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs font-semibold text-[color:var(--kinvera-text)] group-hover:text-teal-700 truncate">
                        {sample.title}
                      </h4>
                      <p className="text-[11px] text-slate-600 line-clamp-2 mt-0.5">
                        {sample.description}
                      </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-teal-600 shrink-0" />
                  </div>
                ))}
              </div>
            </div>

            {/* Core Tenets */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-left w-full pt-2">
              <div className="p-3.5 rounded-xl kin-studio-panel-subtle">
                <span className="text-xs font-semibold text-[color:var(--kinvera-text)] flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-teal-600" /> Strict Identity Fidelity
                </span>
                <p className="text-[11px] text-slate-600 mt-1 leading-relaxed">
                  Preserves original facial geometry, soft skin transitions, and gender. No artificial carved wrinkles or generic faces.
                </p>
              </div>
              <div className="p-3.5 rounded-xl kin-studio-panel-subtle">
                <span className="text-xs font-semibold text-[color:var(--kinvera-text)] flex items-center gap-1.5">
                  <Paintbrush className="w-4 h-4 text-teal-600" /> Scratch Inpaint Brush
                </span>
                <p className="text-[11px] text-slate-600 mt-1 leading-relaxed">
                  Paint directly over severe cracks, tears, and tape marks to confine infilling strictly to damaged zones.
                </p>
              </div>
              <div className="p-3.5 rounded-xl kin-studio-panel-subtle">
                <span className="text-xs font-semibold text-[color:var(--kinvera-text)] flex items-center gap-1.5">
                  <Archive className="w-4 h-4 text-teal-600" /> Bulk Batch ZIP Export
                </span>
                <p className="text-[11px] text-slate-600 mt-1 leading-relaxed">
                  Process all queued photos in batch, remove unwanted images, and package all restored files in one click.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ACTIVE WORKSPACE WITH BATCH QUEUE */}
        {queue.length > 0 && activeItem && (
          <div className="space-y-5">
            {/* Batch Queue Drawer */}
            <BatchQueueDrawer
              queue={queue}
              activeId={activeId}
              onSelectPhoto={(id) => {
                setActiveId(id);
                setActiveVersionIndex(0);
              }}
              onRemovePhoto={handleRemovePhotoFromQueue}
              onProcessBatch={handleProcessEntireBatch}
              isProcessingBatch={isProcessingBatch}
              onAddMoreFiles={handleAddFilesToQueue}
              onDownloadAllZip={handleDownloadAllZip}
              isZipping={isZipping}
            />

            {/* Photo Action Bar: Corner-Pinning, Paint Damage Mask, Upright Rotation & Revision Tabs */}
            <div className="kin-studio-toolbar flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between px-3 sm:px-4 py-2.5 gap-2.5 text-xs">
              {/* Boundary Pinning, Inpaint Mask & Rotation Controls */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Interactive Crop & Corner-Pin Button */}
                <button
                  onClick={() => setIsCropModalOpen(true)}
                  disabled={isCurrentProcessing || isRotating}
                  className={`inline-flex items-center space-x-1.5 px-3 py-2 min-h-10 rounded-lg font-semibold text-xs transition-colors border shadow-sm ${
                    activeItem.versions.length > 0
                      ? 'bg-white hover:bg-slate-50 border-slate-300 text-teal-800'
                      : activeItem.isCropped
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100'
                        : 'bg-white hover:bg-slate-50 border-slate-300 text-teal-800'
                  }`}
                  title={
                    activeItem.versions.length > 0
                      ? 'Crop the currently selected restored revision (non-AI)'
                      : 'Manually pin 4 corners or crop surrounding table borders'
                  }
                >
                  <Crop className="w-3.5 h-3.5" />
                  <span>
                    {activeItem.versions.length > 0
                      ? 'Crop Restored'
                      : activeItem.isCropped
                        ? 'Adjust Pins'
                        : 'Crop & Pin'}
                  </span>
                </button>

                {/* Interactive Paint Damage Mask Button */}
                <button
                  onClick={() => setIsMaskModalOpen(true)}
                  disabled={isCurrentProcessing || isRotating}
                  className={`inline-flex items-center space-x-1.5 px-3 py-2 min-h-10 rounded-lg font-semibold text-xs transition-colors border shadow-sm ${
                    activeItem.damageMask
                      ? 'bg-red-50 border-red-300 text-red-800 hover:bg-red-100'
                      : 'bg-white hover:bg-slate-50 border-slate-300 text-slate-700 hover:text-red-700'
                  }`}
                  title="Paint directly over scratches and tears for targeted infilling"
                >
                  <Paintbrush className="w-3.5 h-3.5 text-red-500" />
                  <span>{activeItem.damageMask ? 'Edit Mask' : 'Damage Mask'}</span>
                </button>

                {activeItem.damageMask && (
                  <button
                    onClick={handleClearDamageMask}
                    disabled={isCurrentProcessing || isRotating}
                    className="px-2 py-1 text-[11px] text-slate-500 hover:text-red-700 rounded hover:bg-red-50 transition-colors"
                    title="Remove painted damage mask"
                  >
                    Clear Mask
                  </button>
                )}

                {activeItem.isCropped && activeItem.versions.length === 0 && (
                  <button
                    onClick={handleResetCrop}
                    disabled={isCurrentProcessing || isRotating}
                    className="px-2 py-1 text-[11px] text-slate-500 hover:text-slate-800 rounded hover:bg-slate-100 transition-colors"
                    title="Revert to original full image"
                  >
                    Reset Crop
                  </button>
                )}

                <div className="h-4 w-px bg-slate-200 hidden sm:block" />

                {/* Upright Rotation Controls */}
                <span className="text-slate-500 font-medium hidden sm:inline">Rotate Upright:</span>
                <div className="inline-flex items-center bg-slate-50 rounded-lg p-0.5 border border-slate-200">
                  <button
                    onClick={() => handleRotateActivePhoto('ccw')}
                    disabled={isCurrentProcessing || isRotating}
                    className="inline-flex items-center space-x-1 px-2.5 py-2 min-h-10 text-slate-700 hover:text-teal-800 hover:bg-white rounded transition-colors disabled:opacity-40"
                    title="Rotate 90° Counter-Clockwise"
                  >
                    {isRotating ? (
                      <RefreshCw className="w-3.5 h-3.5 text-teal-600 animate-spin" />
                    ) : (
                      <RotateCcw className="w-3.5 h-3.5 text-teal-600" />
                    )}
                    <span>90° Left</span>
                  </button>
                  <button
                    onClick={() => handleRotateActivePhoto('cw')}
                    disabled={isCurrentProcessing || isRotating}
                    className="inline-flex items-center space-x-1 px-2.5 py-2 min-h-10 text-slate-700 hover:text-teal-800 hover:bg-white rounded transition-colors disabled:opacity-40"
                    title="Rotate 90° Clockwise"
                  >
                    {isRotating ? (
                      <RefreshCw className="w-3.5 h-3.5 text-teal-600 animate-spin" />
                    ) : (
                      <RotateCw className="w-3.5 h-3.5 text-teal-600" />
                    )}
                    <span>90° Right</span>
                  </button>
                </div>

                {activeItem.rotation !== 0 && (
                  <span className="text-[11px] text-teal-800 font-mono px-2 py-0.5 bg-teal-50 rounded border border-teal-200">
                    Rotated {activeItem.rotation}°
                  </span>
                )}
              </div>

              {/* Revision History Tabs (if already restored) */}
              <div className="flex items-center w-full sm:w-auto">
                {activeItem.versions.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap w-full">
                    <span className="text-slate-500 shrink-0">Revisions:</span>
                    {activeItem.versions.map((ver, idx) => (
                      <button
                        key={ver.id}
                        onClick={() => setActiveVersionIndex(idx)}
                        className={`px-2.5 py-2 min-h-10 rounded-lg text-xs font-medium transition-colors ${
                          activeVersionIndex === idx
                            ? 'bg-teal-600 text-white font-bold shadow'
                            : 'bg-slate-100 text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        {ver.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Main Interactive Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              {/* Left Column: Visual Before/After Canvas & Diagnostic Card */}
              <div className="lg:col-span-7 space-y-4">
                {activeItem.versions.length > 0 ? (
                  (() => {
                    const activeVersion =
                      activeItem.versions[activeVersionIndex] ||
                      activeItem.versions[activeItem.versions.length - 1];
                    const activeUpscale =
                      activeUpscaleId &&
                      (activeItem.upscaledAssets || []).find((a) => a.id === activeUpscaleId);
                    const comparingUpscale = Boolean(activeUpscale?.imageUrl);
                    return (
                      <ComparisonSlider
                        originalUrl={
                          comparingUpscale
                            ? activeVersion.imageUrl
                            : activeItem.sourceImage.dataUrl
                        }
                        restoredUrl={
                          comparingUpscale
                            ? activeUpscale!.imageUrl!
                            : activeVersion.imageUrl
                        }
                        onDownload={handleDownloadActiveImage}
                        versionLabel={
                          comparingUpscale
                            ? `Upscaled ${activeUpscale!.factor}×`
                            : activeVersion.label
                        }
                        beforeLabel={comparingUpscale ? 'Restored' : 'Original'}
                        afterLabel={
                          comparingUpscale ? `Upscaled ${activeUpscale!.factor}×` : 'Restored'
                        }
                        downloadLabel={comparingUpscale ? 'Download Upscaled' : 'Download'}
                        exportActions={
                          <GooglePhotosSaveButton
                            variant="toolbar"
                            imageUrl={
                              comparingUpscale
                                ? activeUpscale!.imageUrl!
                                : activeVersion.imageUrl
                            }
                            fileName={
                              comparingUpscale
                                ? `kinvera-${activeItem.fileName.replace(/\.[^/.]+$/, '')}-upscaled-${activeUpscale!.factor}x.png`
                                : `kinvera-${activeItem.fileName}`
                            }
                            watermarkExportsEnabled={watermarkExportsEnabled}
                          />
                        }
                      />
                    );
                  })()
                ) : (
                  <div className="kin-studio-photo-stage w-full h-[450px] flex flex-col items-center justify-center p-6 text-center space-y-4 relative overflow-hidden">
                    <div className="relative inline-block max-h-72">
                      <img
                        src={activeItem.sourceImage.dataUrl}
                        alt={activeItem.fileName}
                        className={`max-h-72 object-contain rounded border border-stone-700 select-none transition-transform duration-200 ${
                          isCurrentProcessing ? 'opacity-40 filter blur-[1px]' : 'opacity-90'
                        }`}
                      />

                      {/* Display painted damage mask overlay if present */}
                      {activeItem.damageMask && !isCurrentProcessing && (
                        <img
                          src={activeItem.damageMask.dataUrl}
                          alt="Painted Mask Overlay"
                          className="absolute inset-0 w-full h-full object-contain pointer-events-none rounded opacity-80"
                        />
                      )}
                    </div>

                    {isCurrentProcessing ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/75 backdrop-blur-sm rounded p-4 text-center">
                        <RefreshCw className="w-8 h-8 text-teal-400 animate-spin mb-2" />
                        <span className="text-sm font-semibold text-stone-100">
                          {activeItem.status === 'analyzing'
                            ? 'Conducting Forensic Image Inspection...'
                            : 'Executing Conservative Archival Restoration...'}
                        </span>
                        <span className="text-xs text-stone-400 mt-1 max-w-xs">
                          {activeItem.status === 'analyzing'
                            ? 'Detecting photograph medium, apparent age, and subject gender...'
                            : activeItem.damageMask
                            ? 'Focusing deep infilling on painted damage mask zones...'
                            : 'Repairing scratches, perspective angle, and preserving authentic skin gradients.'}
                        </span>
                      </div>
                    ) : (
                      <div className="bg-stone-950/90 px-4 py-2.5 rounded-xl border border-stone-800 max-w-md shadow-xl text-center space-y-1">
                        <p className="text-xs font-semibold flex items-center justify-center gap-1.5 text-teal-300">
                          <Eye className="w-4 h-4" />
                          <span>Forensic Inspection Ready</span>
                        </p>
                        <p className="text-[11px] text-stone-400 leading-relaxed">
                          Verify the subject's gender &amp; medium below. Use the <strong>Approve &amp; Restore</strong> button in the console to the right when you are ready.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Diagnostic Analysis Card with Subject Gender/Age Editor */}
                {activeItem.analysis && (
                  <AnalysisCard
                    analysis={activeItem.analysis}
                    status={activeItem.status}
                    onMediumChange={handleMediumReclassify}
                    onSubjectUpdate={handleSubjectUpdate}
                    onAddSubject={handleAddSubject}
                    onAddSubjectManual={handleAddSubject}
                    onLocateSubject={(index) => {
                      setLocatingPersonIndex(index);
                      setSubjectPlacementMode(true);
                    }}
                    locatingPersonIndex={locatingPersonIndex}
                    onCancelSubjectPlacement={() => {
                      setSubjectPlacementMode(false);
                      setLocatingPersonIndex(null);
                    }}
                    onPlaceSubjectFace={handlePlaceSubjectFace}
                    subjectPlacementMode={subjectPlacementMode}
                    onRemoveSubject={handleRemoveSubject}
                    hasRestoredVersions={activeItem.versions.length > 0}
                    analysisDirty={analysisDirty}
                    onRerenderWithUpdatedSettings={handleRerenderWithUpdatedSettings}
                    analysisImageUrl={activeItem.previewImage?.dataUrl || activeItem.sourceImage.dataUrl}
                    isProcessing={isCurrentProcessing}
                  />
                )}
              </div>

              {/* Right Column: Conversational Follow-Up & Primary Approval Console */}
              <div className="lg:col-span-5 space-y-4">
                <ChatFollowUp
                  messages={activeItem.messages}
                  onSendMessage={handleFollowUpInstruction}
                  isProcessing={isCurrentProcessing}
                  status={activeItem.status}
                  hasRestoredVersions={activeItem.versions.length > 0}
                  analysisDirty={analysisDirty}
                  analysis={activeItem.analysis}
                  onRerenderWithUpdatedSettings={handleRerenderWithUpdatedSettings}
                  onApproveInitialRestoration={(customInstruction, referenceImage) =>
                    executeRestorationForItem(activeItem, customInstruction, referenceImage)
                  }
                />

                {activeItem.versions.length > 0 && (
                  <ShareActions
                    imageUrl={
                      (activeUpscaleId &&
                        (activeItem.upscaledAssets || []).find((a) => a.id === activeUpscaleId)
                          ?.imageUrl) ||
                      activeItem.versions[activeVersionIndex]?.imageUrl
                    }
                    fileName={
                      activeUpscaleId
                        ? `kinvera-${activeItem.fileName.replace(/\.[^/.]+$/, '')}-upscaled.png`
                        : `kinvera-${activeItem.fileName}`
                    }
                    watermarkExportsEnabled={watermarkExportsEnabled}
                  />
                )}

                {activeItem.versions.length > 0 && (
                  <ResolutionPanel
                    resolution={activeResolution}
                    upscaleEnabled={upscaleEnabled && upscaleCapabilities.enabled}
                    loading={isUpscaling}
                    activeUpscaleFactor={
                      activeUpscaleId
                        ? (activeItem.upscaledAssets || []).find((a) => a.id === activeUpscaleId)
                            ?.factor ?? null
                        : null
                    }
                    onUpscale={() => {
                      setUpscaleError(null);
                      setIsUpscaleOpen(true);
                    }}
                  />
                )}

                {(activeItem.upscaledAssets || []).length > 0 && (
                  <div className="kin-studio-panel-subtle p-3 space-y-2">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                      Upscaled derivatives
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        onClick={() => setActiveUpscaleId(null)}
                        className={`px-2.5 py-1.5 min-h-9 rounded-lg text-xs font-medium ${
                          !activeUpscaleId
                            ? 'bg-teal-600 text-white'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        Restored
                      </button>
                      {(activeItem.upscaledAssets || []).map((asset) => (
                        <button
                          key={asset.id}
                          type="button"
                          onClick={() => setActiveUpscaleId(asset.id)}
                          className={`px-2.5 py-1.5 min-h-9 rounded-lg text-xs font-medium ${
                            activeUpscaleId === asset.id
                              ? 'bg-teal-600 text-white'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                          title={`From ${asset.sourceVersionLabel || asset.sourceVersionId}`}
                        >
                          {asset.factor}×
                        </button>
                      ))}
                    </div>
                    {activeUpscaleId &&
                      (() => {
                        const asset = (activeItem.upscaledAssets || []).find(
                          (a) => a.id === activeUpscaleId
                        );
                        if (!asset) return null;
                        const beforePrint = estimatedPrintInchesAtDpi(
                          asset.inputWidth,
                          asset.inputHeight,
                          300
                        );
                        const afterPrint = estimatedPrintInchesAtDpi(
                          asset.outputWidth,
                          asset.outputHeight,
                          300
                        );
                        return (
                          <div className="text-xs text-slate-700 space-y-1.5 pt-1 border-t border-slate-200">
                            <div className="font-bold text-teal-800">
                              UPSCALED {asset.factor}×
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <div className="text-[10px] uppercase text-slate-500">Before</div>
                                <div>
                                  {asset.inputWidth.toLocaleString()} ×{' '}
                                  {asset.inputHeight.toLocaleString()}
                                </div>
                                <div className="text-slate-500">
                                  {megapixelsOf(asset.inputWidth, asset.inputHeight)} MP
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] uppercase text-slate-500">After</div>
                                <div>
                                  {asset.outputWidth.toLocaleString()} ×{' '}
                                  {asset.outputHeight.toLocaleString()}
                                </div>
                                <div className="text-slate-500">
                                  {megapixelsOf(asset.outputWidth, asset.outputHeight)} MP
                                </div>
                              </div>
                            </div>
                            <div className="text-[11px] text-slate-500">
                              Estimated print dimensions at 300 DPI — Before:{' '}
                              {beforePrint.widthIn} × {beforePrint.heightIn} in · After:{' '}
                              {afterPrint.widthIn} × {afterPrint.heightIn} in
                            </div>
                            {asset.sourceVersionLabel ? (
                              <div className="text-[11px] text-slate-500">
                                Source revision: {asset.sourceVersionLabel}
                              </div>
                            ) : null}
                          </div>
                        );
                      })()}
                  </div>
                )}

                {upscaleStatus ? (
                  <div className="rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-xs text-teal-900">
                    {upscaleStatus}
                  </div>
                ) : null}

                {/* Philosophy Card */}
                <div className="kin-studio-panel-subtle p-3.5 text-xs space-y-2">
                  <div className="flex items-center justify-between text-slate-800 font-semibold">
                    <span className="flex items-center gap-1.5 text-teal-700">
                      <ShieldCheck className="w-4 h-4" /> Archival Code of Practice
                    </span>
                    <span className="text-[10px] text-slate-500">Strict Fidelity</span>
                  </div>
                  <p className="text-[11px] text-slate-600 leading-relaxed">
                    Kinvera repairs capture flaws (keystone tilt, glare, scratches, creases, and
                    supported digital defects) while rejecting artificial skin carving, deepened
                    smile lines, or hallucinated facial crevices.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {isUpscaleOpen && (restorationResolution || activeResolution) && (
        <UpscaleDialog
          open={isUpscaleOpen}
          resolution={restorationResolution || activeResolution!}
          capabilities={upscaleCapabilities}
          creditMode={productConfig?.creditMode || 'disabled'}
          stage={productConfig?.stage || 'alpha'}
          creditsEnforced={Boolean(productConfig?.creditsEnforced)}
          restorationsCurrentlyFree={
            productConfig?.restorationsCurrentlyFree !== false || restorationsFree
          }
          busy={isUpscaling}
          error={upscaleError}
          onClose={() => !isUpscaling && setIsUpscaleOpen(false)}
          onConfirm={handleConfirmUpscale}
        />
      )}

      {/* Perspective Corner-Pinning & Crop Studio Modal */}
      {isCropModalOpen && activeItem && cropModalSource && (
        <PerspectiveCropModal
          isOpen={isCropModalOpen}
          sourceImage={cropModalSource}
          initialCorners={activeItem.versions.length > 0 ? null : activeItem.cropCorners}
          editingLabel={cropEditingLabel}
          onApplyCrop={handleApplyCropCorners}
          onClose={() => setIsCropModalOpen(false)}
        />
      )}

      {/* Interactive Damage Mask Brush Painting Modal */}
      {isMaskModalOpen && activeItem && (
        <DamageMaskModal
          isOpen={isMaskModalOpen}
          sourceImage={activeItem.sourceImage}
          initialMask={activeItem.damageMask}
          onApplyMask={handleApplyDamageMask}
          onClose={() => setIsMaskModalOpen(false)}
        />
      )}

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-4 px-4 text-center text-xs text-slate-500">
        <p>Kinvera Studio · Powered by Gemini 3.1 Flash Image</p>
      </footer>
    </div>
  );
};

